import {FC, useMemo} from 'react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import {dateFormat} from '../../constants/constants';
import {Link} from 'react-router-dom';
import processesRoutes from './routes';
import {getState} from '../common/time_progress_bar/helpers/getState';
import useTimer, {getDatesDifferenceInSeconds} from '../../hooks/useTimer';
import {AlertSettingsResponseProps} from '../settings/alert_alarm/types';
import {calculateAlertRange} from '../common/time_progress_bar/helpers/calculateAlertRange';
import {calculateAndFormatTimeHHMM} from './helpers';

dayjs.extend(duration);

type ProcessesElementProps = {
    id: number,
    targetTime: number,
    startedAt: string,
    patient: {
      id: number,
      firstName: string,
      lastName: string
    },
    currentTask: {
      id: number,
      type: {
        code: string,
        description: string
      }
    },
    processesAlertData: Nullable<AlertSettingsResponseProps>,
    completedAt: Nullable<string>,
    processCode: string
  }

export const ProcessesElement:FC<ProcessesElementProps> =
    ({
      id,
      targetTime,
      startedAt,
      patient,
      currentTask,
      processesAlertData,
      processCode,
      completedAt
    }) => {
      const formattedStartAt = dayjs(startedAt).format(dateFormat);
      const formattedTargetTime = dayjs(formattedStartAt)
          .add(targetTime, 's');

      const currentAlertData = processesAlertData
          ?.find(({processType}) => processType === processCode);

      const alertRange = useMemo(() => {
        if (!currentAlertData) return 0;

        const {relative, relativeRange, absoluteRange} = currentAlertData;
        return calculateAlertRange(
            relative,
            relativeRange,
            absoluteRange,
            targetTime
        );
      }, [JSON.stringify(processesAlertData)]);

      const initialDatesDiff = useMemo(() => {
        return getDatesDifferenceInSeconds(startedAt, completedAt);
      }, [startedAt, formattedTargetTime]);

      const {timeDiffInSeconds} = useTimer({
        initialDiff: initialDatesDiff,
        initialActive: !!startedAt
      });

      const {color} = getState({
        currentTime: timeDiffInSeconds,
        alertRange,
        targetTime,
        isCurrentlyRunning: true
      });

      const expiredTime = useMemo(() => {
        return calculateAndFormatTimeHHMM(timeDiffInSeconds - targetTime, true);
      }, [timeDiffInSeconds, targetTime]);

      const duration = useMemo(() => {
        return calculateAndFormatTimeHHMM(timeDiffInSeconds);
      }, [timeDiffInSeconds, targetTime]);

      return (
        <Link to={processesRoutes.runningItem(id)}>
          <div className="processes-element">
            <div>
              <p className="processes-element_name">
                {patient && `${patient.firstName} ${patient.lastName}`}
              </p>
              <p className="processes-element_start-time">
                {formattedStartAt && formattedStartAt}
              </p>
              <p className="processes-element_task-name">
                {currentTask && currentTask.type.description}
              </p>
            </div>
            <div className={`processes-element_duration ${color}`}>
              {duration}
              <p className="processes-element_expired">
                {expiredTime && `+${expiredTime}`}
              </p>
            </div>
          </div>
        </Link>
      );
    };

export default ProcessesElement;
